import { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { notification_color_keys, useNotifications } from "utils/hooks";
import { dataProvider } from "data";
import { apiMethods } from "data/api.constants";
import { isRequestSuccessful } from "utils/Utils";
import { SOMETHING_WENT_WRONG } from "constants/displayStrings";
import { thank_you_pages_apis } from "features/ThankYouPages/constants/ThankYouPages.api";
import api from "data/APIs";
import { listing_apis } from "features/Listings/Listings.constants";
import {
  FETCH_PAYMENT_LINK,
  PAYMENT_LINK_APIS,
} from "features/PaymentLinks/constants/PaymentLinks.apis";
import {
  GJS_THANKYOU_BUILDER_ROUTE_GENERATORS,
  GJS_THANKYOU_BUILDER_ROUTE_KEYS,
} from "features/ThankYouPages/constants/ThankYouBuilder.constants";
import { navigateTo } from "utils/urlUtils";
import { app_route_keys } from "constants/urlPaths";
import { logError } from "utils/error";
import {
  THANK_YOU_BUILDER_CHOICE_TYPES,
  THANK_YOU_BUILDER_CREATE_FROM_SCRATCH_VALUE,
  THANK_YOU_BUILDER_QUERY_KEYS,
  THANK_YOU_PAGE_TYPE_VALUES,
  THANK_YOU_PAGE_TYPE_STRINGS,
  THANK_YOU_BUILDER_CHOICE_MODAL_TEXTS,
} from "./ThankYouBuilderChoiceModal.constants";

// Classic (1) / Advanced (2) builder source persisted in
// listing.metadata + dynamic_link.metadata so the choice survives reload.
// Canonical key first, then compat keys honoured by the storefront resolver.
const CLASSIC_BUILDER_SOURCE = 1;
const THANKYOU_BUILDER_METADATA_KEYS = [
  "thankyou_builder_type",
  "thankyou_page_builder_source",
  "page_builder_source",
];

const resolveSavedBuilderChoice = (metadata) => {
  if (!metadata || typeof metadata !== "object") return null;
  for (const key of THANKYOU_BUILDER_METADATA_KEYS) {
    const num = Number(metadata[key]);
    if (num === 2) return THANK_YOU_BUILDER_CHOICE_TYPES.ADVANCED;
    if (num === CLASSIC_BUILDER_SOURCE)
      return THANK_YOU_BUILDER_CHOICE_TYPES.CLASSIC;
  }
  return null;
};

/**
 * Builder choice modal:
 * - Fetches GJS pages for listing/payment-link to decide defaults.
 * - Also fetches saved builder source from listing/payment-link metadata so
 *   the choice survives reload (saved CLASSIC wins over pages list).
 * - Advanced + existing pages: shows dropdown (default = published page, else
 *   "Create from scratch"). Continue either navigates to existing builder page
 *   or redirects to thank-you-pages/create with listing/payment query so the
 *   gallery's create modal is name-only.
 * - Advanced + no existing pages: no dropdown, Continue redirects straight to
 *   thank-you-pages/create with query.
 * - Classic: persists CLASSIC (1) into listing/payment-link metadata via
 *   update API, unpublishes live page (if any) then goes to classic editor.
 */
export const useThankYouBuilderChoiceModal = ({
  listingUuid,
  paymentLinkUuid,
  isCombo = false,
  pageType,
  open,
  onClose,
}) => {
  const history = useHistory();
  const { notify } = useNotifications();

  const [selectedBuilder, setSelectedBuilder] = useState(null);
  const [selectedPageUuid, setSelectedPageUuid] = useState(null);
  const [thankYouPages, setThankYouPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);

  // Listing and payment-link pages are independent: when a payment link is in
  // context, scope everything (dropdown list, create flow, classic editor) to
  // the link's own pages — never the listing's pages, even for the same listing.
  const hasPaymentLink = !!paymentLinkUuid;

  // normalize pageType (from THANKYOU_PAGE_TYPES.*.key "purchase"/"optin" or 1/2) to 1/2 for API
  const normalizedPageType = (() => {
    if (pageType == null || pageType === "") return null;
    const v = String(pageType).toLowerCase();
    if (
      v === String(THANK_YOU_PAGE_TYPE_VALUES.OPTIN) ||
      v === THANK_YOU_PAGE_TYPE_STRINGS.OPTIN ||
      v === THANK_YOU_PAGE_TYPE_STRINGS.OPT_IN
    )
      return THANK_YOU_PAGE_TYPE_VALUES.OPTIN;
    if (
      v === String(THANK_YOU_PAGE_TYPE_VALUES.PURCHASE) ||
      v === THANK_YOU_PAGE_TYPE_STRINGS.PURCHASE
    )
      return THANK_YOU_PAGE_TYPE_VALUES.PURCHASE;
    const n = Number(pageType);
    if (
      n === THANK_YOU_PAGE_TYPE_VALUES.PURCHASE ||
      n === THANK_YOU_PAGE_TYPE_VALUES.OPTIN
    )
      return n;
    return null;
  })();

  // Saved builder source from listing / payment-link metadata so the modal
  // preselects the persisted choice on reload.
  const fetchSavedBuilderChoice = useCallback(async () => {
    try {
      if (hasPaymentLink && paymentLinkUuid) {
        const res = await dataProvider.custom_request(
          `${FETCH_PAYMENT_LINK}/${paymentLinkUuid}`,
          apiMethods.GET
        );
        if (isRequestSuccessful(res?.status)) {
          return resolveSavedBuilderChoice(res?.data?.metadata);
        }
      } else if (listingUuid) {
        const res = await dataProvider.custom_request(
          `${listing_apis.get_listing_details}/${listingUuid}`,
          apiMethods.GET
        );
        if (isRequestSuccessful(res?.status)) {
          return resolveSavedBuilderChoice(res?.data?.event?.metadata);
        }
      }
    } catch {
      // silent fail — fall back to pages-list defaults
    }
    return null;
  }, [hasPaymentLink, listingUuid, paymentLinkUuid]);

  // Persist builder source into listing / payment-link metadata (update API).
  // Payment-link (incl. combo) scope wins when a payment link is in context.
  const persistBuilderSource = useCallback(
    async (source) => {
      const metadata = {};
      for (const key of THANKYOU_BUILDER_METADATA_KEYS) {
        metadata[key] = source;
      }
      try {
        if (hasPaymentLink && paymentLinkUuid) {
          await dataProvider.custom_request(
            PAYMENT_LINK_APIS.update_payment_link,
            apiMethods.POST,
            {
              dynamic_link_id: paymentLinkUuid,
              updated_values: { metadata },
            }
          );
        } else if (listingUuid) {
          await dataProvider.custom_request(
            api.update_listing,
            apiMethods.POST,
            {
              uuid: listingUuid,
              updated_values: { metadata },
            }
          );
        }
      } catch (e) {
        logError({
          error: e,
          occuredAt: "useThankYouBuilderChoiceModal.persistBuilderSource",
        });
      }
    },
    [hasPaymentLink, listingUuid, paymentLinkUuid]
  );

  useEffect(() => {
    if (!open) return;
    if (!listingUuid && !paymentLinkUuid) return;
    let cancelled = false;
    setSelectedBuilder(null);
    setSelectedPageUuid(null);
    setThankYouPages([]);
    const fetchPages = async () => {
      setLoading(true);
      try {
        const baseParams = hasPaymentLink
          ? { payment_link_uuid: paymentLinkUuid }
          : { listing_uuid: listingUuid };
        const queryParams = {
          ...baseParams,
          ...(normalizedPageType
            ? {
                page_type: normalizedPageType,
                thankyou_page_type: normalizedPageType,
              }
            : {}),
        };
        const [res, savedChoice] = await Promise.all([
          dataProvider.custom_request(
            thank_you_pages_apis.get_thank_you_pages_list,
            apiMethods.GET,
            queryParams
          ),
          fetchSavedBuilderChoice(),
        ]);
        if (!cancelled && isRequestSuccessful(res?.status)) {
          const pages = res?.data?.thankyou_pages || [];
          setThankYouPages(pages);
          const livePage = pages.find((p) => p.is_published);
          if (savedChoice === THANK_YOU_BUILDER_CHOICE_TYPES.CLASSIC) {
            // persisted classic choice survives reload even when GJS rows exist
            setSelectedBuilder(THANK_YOU_BUILDER_CHOICE_TYPES.CLASSIC);
            setSelectedPageUuid(null);
          } else if (savedChoice === THANK_YOU_BUILDER_CHOICE_TYPES.ADVANCED) {
            setSelectedBuilder(THANK_YOU_BUILDER_CHOICE_TYPES.ADVANCED);
            setSelectedPageUuid(livePage?.uuid || null);
          } else if (livePage) {
            setSelectedBuilder(THANK_YOU_BUILDER_CHOICE_TYPES.ADVANCED);
            setSelectedPageUuid(livePage.uuid);
          } else if (pages.length > 0) {
            // pages exist but none published — default to advanced + create-from-scratch
            setSelectedBuilder(THANK_YOU_BUILDER_CHOICE_TYPES.ADVANCED);
            setSelectedPageUuid(null);
          } else {
            setSelectedBuilder(THANK_YOU_BUILDER_CHOICE_TYPES.CLASSIC);
            setSelectedPageUuid(null);
          }
        }
      } catch {
        // silent fail — user can still choose
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchPages();
    return () => {
      cancelled = true;
    };
  }, [
    open,
    listingUuid,
    paymentLinkUuid,
    normalizedPageType,
    fetchSavedBuilderChoice,
    hasPaymentLink,
  ]);

  const handleClose = useCallback(() => {
    setThankYouPages([]);
    if (onClose) onClose();
  }, [onClose]);

  const handleBuilderChange = useCallback(
    (builder) => {
      setSelectedBuilder(builder);
      if (builder === THANK_YOU_BUILDER_CHOICE_TYPES.CLASSIC) {
        setSelectedPageUuid(null);
      } else {
        // switching to advanced: default to published page if any, else create-from-scratch
        const livePage = thankYouPages.find((p) => p.is_published);
        setSelectedPageUuid(livePage?.uuid || null);
      }
    },
    [thankYouPages]
  );

  const redirectToCreateGallery = useCallback(() => {
    handleClose();
    const extra = normalizedPageType
      ? { [THANK_YOU_BUILDER_QUERY_KEYS.PAGE_TYPE]: normalizedPageType }
      : {};
    if (hasPaymentLink) {
      navigateTo(app_route_keys.thank_you_pages_create, {
        queryParams: {
          [THANK_YOU_BUILDER_QUERY_KEYS.PAYMENT_LINK_UUID]: paymentLinkUuid,
          ...extra,
        },
      });
    } else {
      navigateTo(app_route_keys.thank_you_pages_create, {
        queryParams: {
          [THANK_YOU_BUILDER_QUERY_KEYS.LISTING_UUID]: listingUuid,
          ...extra,
        },
      });
    }
  }, [
    handleClose,
    hasPaymentLink,
    listingUuid,
    paymentLinkUuid,
    normalizedPageType,
  ]);

  const handleContinue = useCallback(async () => {
    if (!selectedBuilder) return;
    setProcessing(true);
    try {
      if (selectedBuilder === THANK_YOU_BUILDER_CHOICE_TYPES.CLASSIC) {
        // Persist CLASSIC choice in listing / payment-link metadata BEFORE
        // redirect so reload keeps classic selected even with no live page.
        await persistBuilderSource(CLASSIC_BUILDER_SOURCE);
        const livePage = thankYouPages.find((p) => p.is_published);
        if (livePage) {
          await dataProvider.custom_request(
            thank_you_pages_apis.unpublish_thank_you_page,
            apiMethods.POST,
            { thankyou_page_uuid: livePage.uuid }
          );
        }
        if (hasPaymentLink && isCombo) {
          navigateTo(app_route_keys.update_thankyou, {
            args: {
              combo_uuid: paymentLinkUuid,
              type: pageType,
              isCombo: true,
            },
          });
        } else if (hasPaymentLink) {
          navigateTo(app_route_keys.update_thankyou, {
            args: {
              listing_uuid: listingUuid,
              dynamic_link_uid: paymentLinkUuid,
              type: pageType,
              isCombo: false,
            },
          });
        } else {
          navigateTo(app_route_keys.update_thankyou, {
            args: { listing_uuid: listingUuid, type: pageType },
          });
        }
        handleClose();
      } else if (selectedBuilder === THANK_YOU_BUILDER_CHOICE_TYPES.ADVANCED) {
        // no existing pages -> straight to gallery (name-only flow)
        if (thankYouPages.length === 0) {
          redirectToCreateGallery();
          return;
        }
        // create-from-scratch selected -> gallery with params
        if (
          !selectedPageUuid ||
          selectedPageUuid === THANK_YOU_BUILDER_CREATE_FROM_SCRATCH_VALUE
        ) {
          redirectToCreateGallery();
          return;
        }
        // existing page selected -> builder
        history.push(
          GJS_THANKYOU_BUILDER_ROUTE_GENERATORS[
            GJS_THANKYOU_BUILDER_ROUTE_KEYS.update
          ]({ thankYouPageUuid: selectedPageUuid })
        );
        handleClose();
      }
    } catch (e) {
      notify(e?.message || SOMETHING_WENT_WRONG, notification_color_keys.error);
      logError({
        error: e,
        occuredAt: "useThankYouBuilderChoiceModal.handleContinue",
      });
    } finally {
      setProcessing(false);
    }
  }, [
    selectedBuilder,
    selectedPageUuid,
    thankYouPages,
    listingUuid,
    paymentLinkUuid,
    hasPaymentLink,
    isCombo,
    pageType,
    history,
    notify,
    handleClose,
    redirectToCreateGallery,
    persistBuilderSource,
  ]);

  const primaryBtnText =
    selectedBuilder === THANK_YOU_BUILDER_CHOICE_TYPES.ADVANCED &&
    selectedPageUuid &&
    selectedPageUuid !== THANK_YOU_BUILDER_CREATE_FROM_SCRATCH_VALUE
      ? THANK_YOU_BUILDER_CHOICE_MODAL_TEXTS.CONTINUE_EDITING
      : THANK_YOU_BUILDER_CHOICE_MODAL_TEXTS.CONTINUE;

  return {
    selectedBuilder,
    setSelectedBuilder: handleBuilderChange,
    selectedPageUuid,
    setSelectedPageUuid,
    thankYouPages,
    loading,
    processing,
    handleContinue,
    handleClose,
    primaryBtnText,
  };
};
